import React from 'react';
import { Box, Snackbar, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { capitalize } from 'lodash';
import { SET_CHECKLIST_TASK } from '../../../queries';
import { useHubPetParent } from '../HubPetParentContext';
import { PetParentAnimal } from '../../../types/PetParent';
import HubSectionContainer from '../CustomContainer';
import TrainerContactBanner from '../Home/Checklist/TrainerContactBanner';
import { fillPet } from '../Home/checklistUtils';
import useHubPageView from '../useHubPageView';
import { track } from '../analytics';
import {
  resolveGuides, resolveQuestionGroups, Species,
} from './data';
import ResourceLibrary, { guideTaskKey } from './ResourceLibrary';
import NextUpGuide from './NextUpGuide';
import PartnerNote from './PartnerNote';
import RehomingSection from './RehomingSection';

type CheckedMap = Record<string, boolean>;

/**
 * Pre-surrender Hub home (ticket 2530): reason-filtered resource library,
 * rehoming partner links, and adopter-question guidance for pet parents
 * considering surrender. Rendered from the /hub/home route instead of the
 * standard 30-Day Checklist experience (see Home/index.tsx).
 */
function PreSurrenderHome({ animal }: { animal: PetParentAnimal }) {
  useHubPageView();
  const { petParent, loading } = useHubPetParent();

  const species: Species = ['cat', 'kitten'].includes(animal.animalType?.toLowerCase()) ? 'cat' : 'dog';
  // Pre-surrender names are parent-supplied via Typeform, so — unlike the
  // adopter checklist — the real name is trustworthy here.
  const petName = capitalize(animal.name?.trim()) || `your ${species}`;
  const reasons = React.useMemo(() => animal.surrenderReasons || [], [animal.surrenderReasons]);

  const guides = React.useMemo(
    () => fillPet(resolveGuides(species, reasons), petName),
    [species, reasons, petName],
  );
  const questionGroups = React.useMemo(
    () => fillPet(resolveQuestionGroups(species, reasons), petName),
    [species, reasons, petName],
  );

  const expired = petParent?.membershipStatus === 'expired';
  const orgName = petParent?.awo?.displayName ?? petParent?.awo?.name ?? 'your shelter';

  // Server-persisted reviewed state, overlaid with this session's optimistic
  // toggles so checking feels instant while the mutation is in flight.
  const serverChecked = React.useMemo(() => {
    const map: CheckedMap = {};
    (petParent?.checklistCompletedTasks || []).forEach((key: string) => { map[key] = true; });
    return map;
  }, [petParent?.checklistCompletedTasks]);
  const [optimistic, setOptimistic] = React.useState<CheckedMap>({});
  const checked = React.useMemo(
    () => ({ ...serverChecked, ...optimistic }),
    [serverChecked, optimistic],
  );

  const [expanded, setExpanded] = React.useState<CheckedMap>({});
  const expandedInitialized = React.useRef(false);

  const [toast, setToast] = React.useState<string | null>(null);
  const [setChecklistTask] = useMutation(SET_CHECKLIST_TASK);

  const done = guides.filter((g) => checked[guideTaskKey(g.slug)]).length;
  const total = guides.length;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  const nextGuide = guides.find((g) => !checked[guideTaskKey(g.slug)]) || null;

  // A lesson opened from a guide card sends the parent back to
  // /hub/home?task=psr-<slug>, so returning reopens the guide they were reading
  // rather than resetting to the first unreviewed one (same contract as the
  // 30-Day Checklist).
  const [params, setParams] = useSearchParams();
  const requestedTask = params.get('task');

  React.useEffect(() => {
    if (expandedInitialized.current || loading || !petParent) return;
    expandedInitialized.current = true;
    const target = (requestedTask && guides.some((g) => guideTaskKey(g.slug) === requestedTask))
      ? requestedTask
      : nextGuide && guideTaskKey(nextGuide.slug);
    if (target) setExpanded({ [target]: true });
  }, [loading, petParent, nextGuide, requestedTask, guides]);

  const guideRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const registerGuideRef = (slug: string, el: HTMLDivElement | null) => {
    if (el) guideRefs.current[guideTaskKey(slug)] = el;
  };

  // Scroll the requested guide into view, then drop the param — same approach
  // (and reasoning) as Home/index.tsx: poll briefly until the row exists so we
  // don't race ScrollToTop or the accordion render, and use setTimeout rather
  // than rAF so a backgrounded tab still lands on the row.
  const scrolledToTask = React.useRef(false);
  React.useEffect(() => {
    if (scrolledToTask.current || loading || !requestedTask) return undefined;

    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const tryScroll = () => {
      const el = guideRefs.current[requestedTask];
      if (!el) {
        attempts += 1;
        if (attempts > 40) return;
        timer = setTimeout(tryScroll, 25);
        return;
      }
      scrolledToTask.current = true;
      el.scrollIntoView({ block: 'center' });
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('task');
        return next;
      }, { replace: true });
    };
    timer = setTimeout(tryScroll, 0);
    return () => clearTimeout(timer);
  }, [loading, requestedTask, setParams]);

  const clearOptimistic = (taskKey: string) => {
    setOptimistic((s) => {
      const next = { ...s };
      delete next[taskKey];
      return next;
    });
  };

  const onToggleCheck = (slug: string) => {
    const taskKey = guideTaskKey(slug);
    const completed = !checked[taskKey];
    setOptimistic((s) => ({ ...s, [taskKey]: completed }));
    const guideName = guides.find((g) => g.slug === slug)?.title;
    track('presurrender_guide_check', { guide_name: guideName, task_key: taskKey, completed });
    setChecklistTask({
      variables: { taskKey, completed },
      // The mutation returns the full completed-task list; make it the cached
      // server state so the optimistic overlay can be dropped once settled.
      update: (cache, { data }) => {
        const list = data?.setChecklistTask;
        if (list && petParent?.id) {
          cache.modify({
            id: cache.identify({ __typename: 'PetParentSingular', id: petParent.id }),
            fields: { checklistCompletedTasks: () => list },
          });
        }
      },
    })
      .then(() => clearOptimistic(taskKey))
      .catch(() => {
        // Revert to server truth so the UI never silently shows unsaved state.
        clearOptimistic(taskKey);
        setToast("We couldn't save that change — please try again.");
      });
  };

  const onToggleExpand = (slug: string) => {
    const taskKey = guideTaskKey(slug);
    setExpanded((s) => ({ ...s, [taskKey]: !s[taskKey] }));
  };

  const goToGuide = () => {
    if (!nextGuide) return;
    const taskKey = guideTaskKey(nextGuide.slug);
    if (!expanded[taskKey]) onToggleExpand(nextGuide.slug);
    requestAnimationFrame(() => {
      const el = guideRefs.current[taskKey];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.animate(
          [{ background: 'rgba(98, 0, 238, 0.12)' }, { background: 'rgba(98, 0, 238, 0)' }],
          { duration: 1200, easing: 'ease-out' },
        );
      }
    });
  };

  if (loading) return null;

  return (
    <Box>
      <Box sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">{`Hi, ${petParent?.firstName}`}</Typography>
          <Typography variant="body2">
            {`We've partnered with ${orgName} to provide you and ${petName} with support.`}
          </Typography>
        </HubSectionContainer>
      </Box>
      {/* No membership upsell here (QA): pre-surrender parents are free Community
          members, so /home always shows the guide hero, expired or not. The trainer
          banner does hide when expired — messaging support has ended. */}
      <HubSectionContainer backgroundContrast wide sx={{ py: 3 }}>
        <NextUpGuide
          guide={nextGuide}
          petName={petName}
          percent={percent}
          done={done}
          total={total}
          onGo={goToGuide}
        />
      </HubSectionContainer>
      {!expired && (
        <HubSectionContainer backgroundContrast wide sx={{ pb: 3.5 }}>
          <TrainerContactBanner />
        </HubSectionContainer>
      )}
      <HubSectionContainer backgroundContrast wide sx={{ pb: 3.5 }}>
        <PartnerNote orgName={orgName} />
      </HubSectionContainer>
      <HubSectionContainer backgroundContrast wide sx={{ pb: 3.5 }}>
        <ResourceLibrary
          petName={petName}
          orgName={orgName}
          guides={guides}
          checked={checked}
          expanded={expanded}
          onToggleCheck={onToggleCheck}
          onToggleExpand={onToggleExpand}
          registerGuideRef={registerGuideRef}
        />
      </HubSectionContainer>
      <HubSectionContainer backgroundContrast wide sx={{ pb: 4 }}>
        <RehomingSection petName={petName} orgName={orgName} questionGroups={questionGroups} />
      </HubSectionContainer>
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
}

export default PreSurrenderHome;
